import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  CognitoIdentityProviderClient,
  ResendConfirmationCodeCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { createRemoteJWKSet, jwtVerify } from 'jose';
import { User, type UserProfile } from 'oidc-client-ts';
import { isNativeCognitoEnabled } from './cognitoFeatureFlag';
import { isValidPostLoginReturnUrl } from './postLoginRedirect';
import { OnboardingError, registerConsumer, requireNativeOnboarding } from './nativeAuthApi';

export interface NativeTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

function config() {
  if (!isNativeCognitoEnabled) throw new Error('Native authentication is disabled');
  const issuer = import.meta.env.VITE_IDP_COGNITO_DOMAIN as string;
  const clientId = import.meta.env.VITE_IDP_COGNITO_CLIENT_ID as string;
  const region = import.meta.env.VITE_IDP_COGNITO_REGION as string;
  const url = new URL(issuer);
  const poolId = url.pathname.slice(1);
  if (url.protocol !== 'https:' || !clientId || !region || !poolId || poolId.includes('/')) {
    throw new Error('Native authentication is not configured');
  }
  return { issuer, clientId, region, poolId };
}

/** SRP uses transient storage; UserManager is the only persisted token owner. */
export async function authenticateNative(email: string, password: string): Promise<NativeTokens> {
  const { poolId, clientId } = config();
  await requireNativeOnboarding();
  const values = new Map<string, string>();
  const storage = {
    getItem: (key: string) => values.get(key) ?? null,
    setItem: (key: string, value: string) => {
      values.set(key, value);
    },
    removeItem: (key: string) => {
      values.delete(key);
    },
    clear: () => values.clear(),
  };
  const pool = new CognitoUserPool({ UserPoolId: poolId, ClientId: clientId, Storage: storage });
  const user = new CognitoUser({ Username: email.trim(), Pool: pool, Storage: storage });
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error, session?: CognitoUserSession) => {
      values.clear();
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (error || !session) reject(error ?? new Error('Sign-in failed'));
      else
        resolve({
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
    };
    const timer = setTimeout(
      () => finish(new Error('Sign-in timed out. Please try again.')),
      15000,
    );
    const unsupported = () =>
      finish(
        Object.assign(new Error('This sign-in needs additional support. Please contact Allume.'), {
          name: 'UnsupportedChallengeError',
        }),
      );
    user.authenticateUser(
      new AuthenticationDetails({ Username: email.trim(), Password: password }),
      {
        onSuccess: (session) => finish(undefined, session),
        onFailure: (error) => finish(error),
        newPasswordRequired: unsupported,
        mfaRequired: unsupported,
        totpRequired: unsupported,
        selectMFAType: unsupported,
        mfaSetup: unsupported,
        customChallenge: unsupported,
      },
    );
  });
}

let jwks: ReturnType<typeof createRemoteJWKSet> | undefined;
let jwksIssuer = '';

export async function nativeTokensToUser(tokens: NativeTokens, returnUrl?: string): Promise<User> {
  const { issuer, clientId } = config();
  if (!jwks || jwksIssuer !== issuer) {
    jwks = createRemoteJWKSet(new URL(`${issuer}/.well-known/jwks.json`));
    jwksIssuer = issuer;
  }
  const [{ payload: access }, { payload: profile }] = await Promise.all([
    jwtVerify(tokens.accessToken, jwks, { issuer, algorithms: ['RS256'] }),
    jwtVerify(tokens.idToken, jwks, { issuer, audience: clientId, algorithms: ['RS256'] }),
  ]);
  if (
    access.token_use !== 'access' ||
    access.client_id !== clientId ||
    profile.token_use !== 'id' ||
    !profile.sub ||
    profile.sub !== access.sub ||
    !Number.isSafeInteger(access.exp) ||
    !Number.isSafeInteger(profile.exp) ||
    !Number.isSafeInteger(access.iat) ||
    !Number.isSafeInteger(access.auth_time) ||
    (access.auth_time as number) > (access.iat as number) ||
    !tokens.refreshToken
  )
    throw new Error('Invalid sign-in response');
  return new User({
    access_token: tokens.accessToken,
    id_token: tokens.idToken,
    refresh_token: tokens.refreshToken,
    token_type: 'Bearer',
    scope: typeof access.scope === 'string' ? access.scope : '',
    expires_at: access.exp,
    profile: profile as UserProfile,
    userState: returnUrl && isValidPostLoginReturnUrl(returnUrl) ? { returnUrl } : undefined,
  });
}

async function send(
  command: (client: CognitoIdentityProviderClient, signal: AbortSignal) => Promise<unknown>,
) {
  const { region } = config();
  await requireNativeOnboarding();
  const client = new CognitoIdentityProviderClient({ region, maxAttempts: 1 });
  try {
    return await command(client, AbortSignal.timeout(10000));
  } finally {
    client.destroy();
  }
}

export async function registerNative(input: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  recaptcha: string;
}) {
  config();
  await requireNativeOnboarding();
  await registerConsumer(input);
}

export async function resendNative(email: string) {
  const { clientId } = config();
  try {
    await send((client, signal) =>
      client.send(
        new ResendConfirmationCodeCommand({ ClientId: clientId, Username: email.trim() }),
        {
          abortSignal: signal,
        },
      ),
    );
  } catch (error) {
    const name = (error as { name?: string } | null)?.name;
    if (name === 'UserNotFoundException' || name === 'InvalidParameterException') return;
    throw error;
  }
}

export function nativeAuthMessage(error: unknown): string {
  if (error instanceof OnboardingError) {
    if (error.status === 409)
      return 'If you already have an account, sign in or request another confirmation email.';
    if (error.status === 400) return 'Please check your registration details and password.';
  }
  const name = error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'Email or password is incorrect.';
    case 'UserNotConfirmedException':
      return 'Please confirm your email before signing in.';
    case 'UsernameExistsException':
      return 'If you already have an account, sign in or use password recovery.';
    case 'CodeMismatchException':
      return 'The verification code is incorrect.';
    case 'ExpiredCodeException':
      return 'The verification code has expired. Request a new email.';
    case 'InvalidPasswordException':
      return 'Please choose a stronger password.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many attempts. Please wait and try again.';
    case 'UnsupportedChallengeError':
      return 'This sign-in needs additional support. Please contact Allume.';
    case 'PasswordResetRequiredException':
      return 'Please use password recovery or contact Allume support.';
    default:
      return 'We could not complete this request. Please try again.';
  }
}
